/**
 * PHOTOS (docs/milestones/PHOTOS-contracts.md §3): the picture taken as a ranger fell, wherever the menus show
 * it - heading his plaque on either wall, full size in a dialog, under his name on the memorial beat, and in the
 * offer to put him on the public wall (aftermath/publishOffer.ts).
 *
 * A picture is always an extra. Screens are built synchronously and the picture arrives when it arrives; one
 * that is not there, or will not load, leaves the screen exactly as it was before photos, without a word.
 */

import './photos.css';
import type { MemorialEntry } from '../../meta/types';
import type { Ctx } from './context';
import { box, button, el } from './dom';
import { fullName, rankAbbr, serviceName } from './text';
import type { PublicName } from './wallPort';
import { dialog } from './widgets/confirm';

/** Where a picture comes from: a URL still on its way, one in hand, or none at all. */
export type PictureSource = Promise<string | null> | string | null;

/** The <img> alt text: what the picture is, for anyone who cannot see it. */
export const PHOTO_ALT = 'TAKEN AS HE FELL';

/** One of this company's dead: his picture from this device or the save server, when the app gave us photos. */
export function companyPicture(ctx: Ctx, entry: MemorialEntry): Promise<string | null> | null {
  const port = ctx.photos;
  return port === undefined || entry.photo === undefined ? null : port.url(entry.photo);
}

/**
 * A name on the public wall. Only a name that went up WITH a picture has one, and only at the wall's own
 * address: the entry's private photo id belongs to another player's save and is never looked up.
 */
export function publicPicture(ctx: Ctx, name: PublicName): string | null {
  const port = ctx.photos;
  return port === undefined || name.photo !== true ? null : port.publicUrl({ by: name.by, id: name.id });
}

/** Calls `draw` with the URL once there is one, or `none` when there is nothing to show. */
export function resolvePicture(source: PictureSource, draw: (url: string) => void, none: () => void): void {
  if (source === null) none();
  else if (typeof source === 'string') draw(source);
  // `url` never rejects by contract; if one ever did, it would mean no picture, not a broken screen.
  else void source.then((url) => (url === null ? none() : draw(url)), none);
}

/**
 * An <img> of one picture, pixelated like the game it came from. The listeners go on before `src`, so neither
 * answer can be missed; `onFail` is for the caller to take it away.
 */
export function photoImage(
  className: string,
  url: string,
  onFail: () => void,
  onLoad?: () => void,
): HTMLImageElement {
  const img = el('img', className);
  img.alt = PHOTO_ALT;
  img.draggable = false;
  img.addEventListener('error', onFail, { once: true });
  if (onLoad !== undefined) img.addEventListener('load', onLoad, { once: true });
  img.src = url;
  return img;
}

/**
 * A place for a picture that may come later: null when there is none to wait for (and then the screen gets no
 * new element at all), otherwise a slot that is filled when the URL arrives and removed when it does not come or
 * will not load.
 */
export function pictureSlot(
  className: string,
  source: PictureSource,
  make: (url: string, fail: () => void) => HTMLElement,
): HTMLElement | null {
  if (source === null) return null;
  const slot = el('div', className);
  const drop = (): void => slot.remove();
  resolvePicture(source, (url) => slot.append(make(url, drop)), drop);
  return slot;
}

/** The picture full size, his name and his cause under it; Esc or CLOSE puts it away. */
export function openPhoto(ctx: Ctx, entry: MemorialEntry, url: string): void {
  const close = (): void => ctx.closeModal();
  const d = dialog('IN MEMORIAM', close, 'menu-dialog-photo');
  const full = photoImage('menu-photo-full', url, () => full.remove());
  const r = entry.ranger;
  d.body.append(
    full,
    box(
      'menu-photo-caption',
      el('div', 'menu-photo-name', `${rankAbbr(r.rank)} ${fullName(r)}`),
      el('div', 'menu-photo-cause', entry.cause),
    ),
  );
  const ok = button('CLOSE', close);
  d.actions.append(ok);
  ctx.openModal({ ...d.modal, initialFocus: () => ok });
}

/** The plaque's thumbnail: a real button, so a click, Enter or Space opens it full size. */
export function photoThumb(ctx: Ctx, entry: MemorialEntry, url: string, fail: () => void): HTMLButtonElement {
  const thumb = el('button', 'menu-photo-thumb');
  thumb.type = 'button';
  thumb.title = 'SEE THE PHOTO';
  thumb.setAttribute('aria-label', `SEE THE PHOTO: ${serviceName(entry.ranger)}`);
  thumb.append(photoImage('menu-photo-img', url, fail));
  thumb.addEventListener('click', () => openPhoto(ctx, entry, url));
  return thumb;
}
